import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { Input } from "@/components/ui/input";
import { AppSidebar } from "@/components/app-sidebar";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  CheckCircle,
  XCircle,
  Eye,
  Clock,
  Package,
  Store,
  User,
  Phone,
  CreditCard,
  Banknote,
  Search,
  Edit,
  Trash2,
  NotebookTabs,
} from "lucide-react";
import Swal from "sweetalert2";

interface SellRequest {
  id: number;
  name: string;
  seller_name: string;
  seller_phone: string; // Changed to string to match database
  product_name: string;
  product_category: string;
  product_price: number;
  product_stock: number;
  seller_no_pegawai: string; // New field for employee number
  seller_divisi: string; // New field for division
  product_description: string;
  product_photo: string;
  bank_account: string;
  provider_bank: string;
  status: string;
  owner_bank_account: number;
  created_at: string;
}

interface Store {
  id: string;
  name: string;
}

const SellRequestManagement = () => {
  const { toast } = useToast();
  const [sellRequests, setSellRequests] = useState<SellRequest[]>([]);
  const [stores, setStores] = useState<Store[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedStore, setSelectedStore] = useState<string>("");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingRequest, setEditingRequest] = useState<SellRequest | null>(
    null
  );

  // Filter requests based on search term and status
  const filteredRequests = sellRequests.filter((request) => {
    const matchesSearch =
      request.product_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      request.seller_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      request.name.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus =
      statusFilter === "all" || request.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  useEffect(() => {
    fetchSellRequests();
    fetchStores();
  }, []);

  const fetchSellRequests = async () => {
    try {
      console.log("Fetching sell requests..."); // Debug 1

      const { data, error } = await supabase
        .from("sell_requests")
        .select("*")
        .order("created_at", { ascending: false });

      console.log("Raw data from Supabase:", data); // Debug 2
      console.log("Error from Supabase:", error); // Debug 3
      console.log("Data type:", typeof data, "Data length:", data?.length); // Debug 4

      if (error) {
        console.error("Supabase error:", error); // Debug 5
        throw error;
      }

      // Map the data to ensure all required fields are present
      const requestsWithDefaults = (data || []).map((req: any) => ({
        id: req.id,
        name: req.name,
        seller_name: req.seller_name,
        seller_phone: req.seller_phone || "", // Keep as string
        seller_divisi: req.seller_divisi,
        seller_no_pegawai: req.seller_no_pegawai
          ? String(req.seller_no_pegawai)
          : "",
        product_name: req.product_name,
        product_category: req.product_category,
        product_price: Number(req.product_price) || 0, // Convert string to number
        product_stock: Number(req.product_stock) || 0,
        product_description: req.product_description || "",
        product_photo: req.product_photo,
        bank_account: req.bank_account,
        provider_bank: req.provider_bank || "",
        owner_bank_account: req.owner_bank_account || "Anomali Data",
        status: req.status,
        created_at: req.created_at,
      }));

      console.log("Mapped data:", requestsWithDefaults); // Debug 6
      setSellRequests(requestsWithDefaults);
    } catch (error: any) {
      console.error("Fetch error:", error); // Debug 7
      toast({
        title: "Error",
        description: "Gagal memuat data permintaan titip jual",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };
  //test
  const fetchStores = async () => {
    try {
      const { data, error } = await supabase
        .from("stores")
        .select("id, name")
        .order("name");

      if (error) throw error;
      setStores(data || []);
    } catch (error: any) {
      console.error("Error fetching stores:", error);
    }
  };

  const updateRequestStatus = async (id: number, status: string) => {
    try {
      const { error } = await supabase
        .from("sell_requests")
        .update({ status })
        .eq("id", id);

      if (error) throw error;

      setSellRequests((prev) =>
        prev.map((req) => (req.id === id ? { ...req, status } : req))
      );

      toast({
        title: "Berhasil",
        description: `Status permintaan berhasil diubah menjadi ${status}`,
        variant: "default",
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: "Gagal mengubah status permintaan",
        variant: "destructive",
      });
    }
  };

  const publishToProducts = async (request: SellRequest) => {
    if (!selectedStore) {
      toast({
        title: "Pilih Toko",
        description: "Silakan pilih toko untuk publish produk",
        variant: "destructive",
      });
      return;
    }

    try {
      // Map category to match database enum
      const categoryMapping: { [key: string]: string } = {
        "Fashion & Pakaian": "merchandise",
        "Elektronik & Gadget": "merchandise",
        "Makanan & Minuman": "makanan",
        "Kesehatan & Kecantikan": "merchandise",
        "Rumah Tangga & Dekorasi": "merchandise",
        "Hobi & Koleksi": "merchandise",
        "Otomotif & Transport": "merchandise",
        "Buku & Edukasi": "merchandise",
        "Olahraga & Outdoor": "merchandise",
        Lainnya: "merchandise",
      };

      const mappedCategory =
        categoryMapping[request.product_category] || "merchandise";

      // Insert into products table
      const { error: insertError } = await supabase.from("products").insert({
        name: request.product_name,
        description: request.product_description,
        price: request.product_price,
        stock: request.product_stock,
        category: mappedCategory as
          | "makanan"
          | "minuman"
          | "merchandise"
          | "kerajinan",
        image_url: request.product_photo
          ? `https://wnxsbentqsmgrhxbkpxi.supabase.co/storage/v1/object/public/sell-products/${request.product_photo}`
          : null,
        store_id: selectedStore,
        created_by: (await supabase.auth.getUser()).data.user?.id || "",
        is_active: true,
      });

      if (insertError) throw insertError;

      // Update sell request status to published
      await updateRequestStatus(request.id, "published");

      toast({
        title: "Berhasil Publish",
        description: "Produk berhasil dipublish ke katalog",
        variant: "default",
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: `Gagal publish produk: ${error.message}`,
        variant: "destructive",
      });
    }
  };

  const MySwal = Swal;
  const handleDeleteRequest = async (id: number) => {
    const result = await MySwal.fire({
      title: "Yakin ingin menghapus?",
      text: "Permintaan ini akan dihapus secara permanen.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3785ceff",
      confirmButtonText: "Ya, hapus!",
      cancelButtonText: "Batal",
    });

    if (!result.isConfirmed) return;

    try {
      const { error } = await supabase
        .from("sell_requests")
        .delete()
        .eq("id", id);

      if (error) throw error;

      setSellRequests((prev) => prev.filter((req) => req.id !== id));

      await MySwal.fire({
        title: "Berhasil",
        text: "Permintaan berhasil dihapus.",
        icon: "success",
        showConfirmButton: false,
        timer: 1500,
      });
    } catch (error: any) {
      await MySwal.fire({
        title: "Gagal",
        text: "Terjadi kesalahan saat menghapus permintaan.",
        icon: "error",
      });
    }
  };
  // const handleDeleteRequest = async (id: number) => {
  //     if (!confirm("Apakah Anda yakin ingin menghapus permintaan ini?")) return;

  //     try {
  //         const { error } = await supabase
  //             .from('sell_requests')
  //             .delete()
  //             .eq('id', id);

  //         if (error) throw error;

  //         setSellRequests(prev => prev.filter(req => req.id !== id));
  //         toast({
  //             title: "Berhasil",
  //             description: "Permintaan berhasil dihapus",
  //             variant: "default",
  //         });
  //     } catch (error: any) {
  //         toast({
  //             title: "Error",
  //             description: "Gagal menghapus permintaan",
  //             variant: "destructive",
  //         });
  //     }
  // };

  const handleEditRequest = (request: SellRequest) => {
    setEditingRequest(request);
    setShowEditDialog(true);
  };

  const handleUpdateRequest = async (formData: FormData) => {
    if (!editingRequest) return;

    try {
      const updatedData = {
        product_name: formData.get("product_name") as string,
        product_description: formData.get("product_description") as string,
        product_price: Number(formData.get("product_price")),
        product_category: formData.get("product_category") as string,
        seller_name: formData.get("seller_name") as string,
        seller_phone: formData.get("seller_phone") as string,
        provider_bank: formData.get("provider_bank") as string,
        bank_account: formData.get("bank_account") as string,
      };

      const { error } = await supabase
        .from("sell_requests")
        .update(updatedData)
        .eq("id", editingRequest.id);

      if (error) throw error;

      setSellRequests((prev) =>
        prev.map((req) =>
          req.id === editingRequest.id ? { ...req, ...updatedData } : req
        )
      );

      setShowEditDialog(false);
      setEditingRequest(null);
      toast({
        title: "Berhasil",
        description: "Data permintaan berhasil diperbarui",
        variant: "default",
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: "Gagal memperbarui data permintaan",
        variant: "destructive",
      });
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "pending":
        return (
          <Badge
            variant="outline"
            className="text-yellow-600 border-yellow-600"
          >
            <Clock className="w-3 h-3 mr-1" />
            Pending
          </Badge>
        );
      case "approved":
        return (
          <Badge variant="outline" className="text-green-600 border-green-600">
            <CheckCircle className="w-3 h-3 mr-1" />
            Approved
          </Badge>
        );
      case "rejected":
        return (
          <Badge variant="outline" className="text-red-600 border-red-600">
            <XCircle className="w-3 h-3 mr-1" />
            Rejected
          </Badge>
        );
      case "published":
        return (
          <Badge variant="outline" className="text-blue-600 border-blue-600">
            <Package className="w-3 h-3 mr-1" />
            Published
          </Badge>
        );
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const formatPhone = (phone: string) => {
    return phone.replace(/(\d{4})(\d{4})(\d+)/, "$1-$2-$3");
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat("id-ID", {
      style: "currency",
      currency: "IDR",
    }).format(price);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <SidebarProvider>
      <div className="flex min-h-screen w-full">
        <AppSidebar />
        <div className="flex-1">
          <header className="h-12 flex items-center border-b">
            <SidebarTrigger className="ml-2" />
            <h1 className="ml-4 font-semibold">Manajemen Titip Jual</h1>
          </header>
          <main className="flex-1">
            <div className="container mx-auto p-6 space-y-6">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h1 className="text-3xl font-display font-bold text-foreground">
                    Manajemen Titip Jual
                  </h1>
                  <p className="text-muted-foreground mt-1">
                    Kelola permintaan titip jual dari pengguna
                  </p>
                </div>
                <Badge variant="outline" className="text-lg px-3 py-1 w-fit">
                  {filteredRequests.length} dari {sellRequests.length}{" "}
                  Permintaan
                </Badge>
              </div>

              <Separator />

              {/* Search and Filters */}
              <Card className="bg-gradient-card border-none shadow-soft">
                <CardContent className="p-4">
                  <div className="flex flex-col lg:flex-row gap-4">
                    {/* Search */}
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                      <Input
                        placeholder="Cari berdasarkan nama produk, penjual, atau toko..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10 w-full"
                      />
                    </div>

                    {/* Status Filter */}
                    <Select
                      value={statusFilter}
                      onValueChange={setStatusFilter}
                    >
                      <SelectTrigger className="w-full lg:w-[150px]">
                        <SelectValue placeholder="Status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Semua Status</SelectItem>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="approved">Approved</SelectItem>
                        <SelectItem value="rejected">Rejected</SelectItem>
                        <SelectItem value="published">Published</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </CardContent>
              </Card>

              {filteredRequests.length === 0 ? (
                <Card>
                  <CardContent className="flex flex-col items-center justify-center py-12">
                    <Package className="h-12 w-12 text-muted-foreground mb-4" />
                    <h3 className="text-lg font-semibold text-muted-foreground">
                      {searchTerm || statusFilter !== "all"
                        ? "Tidak ada permintaan yang ditemukan"
                        : "Belum Ada Permintaan"}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {searchTerm || statusFilter !== "all"
                        ? "Coba ubah filter atau kata kunci pencarian"
                        : "Permintaan titip jual akan muncul di sini"}
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid gap-6 md:grid-cols-2">
                  {filteredRequests.map((request) => (
                    <Card
                      key={request.id}
                      className="shadow-sm border-0 bg-gradient-card"
                    >
                      <CardHeader>
                        <div className="flex items-start justify-between">
                          <div className="space-y-2">
                            <CardTitle className="text-xl font-display">
                              {request.product_name}
                            </CardTitle>
                            <div className="flex flex-wrap gap-x-4 gap-y-2 text-sm text-muted-foreground">
                              <div className="flex items-center gap-1">
                                <Store className="w-4 h-4" />
                                <span>{request.name}</span>
                              </div>
                              <div className="flex items-center gap-1">
                                <User className="w-4 h-4" />
                                <span>{request.seller_name}</span>
                              </div>
                              <div className="flex items-center gap-1">
                                <NotebookTabs className="w-4 h-4" />
                                <span>
                                  {request.seller_no_pegawai} - Divisi{" "}
                                  {request.seller_divisi}
                                </span>
                              </div>
                              <div className="flex items-center gap-1">
                                <Phone className="w-4 h-4" />
                                <span>{formatPhone(request.seller_phone)}</span>
                              </div>
                            </div>
                          </div>
                          <div className="flex flex-col items-end gap-2">
                            {getStatusBadge(request.status)}
                            <span className="text-xs text-muted-foreground">
                              {new Date(request.created_at).toLocaleDateString(
                                "id-ID"
                              )}
                            </span>
                          </div>
                        </div>
                      </CardHeader>

                      <CardContent className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="space-y-3">
                            <div className="flex items-center gap-2">
                              <Banknote className="w-4 h-4 text-accent" />
                              <span className="font-semibold text-lg text-accent">
                                {formatPrice(request.product_price)}
                              </span>
                            </div>
                            {/* Stock Produk */}
                            <div>
                              <p className="text-sm font-medium text-foreground mb-1">
                                Stok Produk:
                              </p>
                              <span className="text-sm text-muted-foreground">
                                {request.product_stock}
                              </span>
                            </div>

                            <div>
                              <p className="text-sm font-medium text-foreground mb-1">
                                Kategori:
                              </p>
                              <Badge variant="secondary">
                                {request.product_category}
                              </Badge>
                            </div>

                            {request.product_description && (
                              <div>
                                <p className="text-sm font-medium text-foreground mb-1">
                                  Deskripsi:
                                </p>
                                <p className="text-sm text-muted-foreground">
                                  {request.product_description}
                                </p>
                              </div>
                            )}

                            <div className="flex items-center gap-2">
                              <CreditCard className="w-4 h-4" />
                              <span className="text-sm">
                                {request.provider_bank} - {request.bank_account}{" "}
                                (A/n {request.owner_bank_account})
                              </span>
                            </div>
                          </div>

                          {request.product_photo && (
                            <div className="flex justify-center">
                              <img
                                src={`https://wnxsbentqsmgrhxbkpxi.supabase.co/storage/v1/object/public/sell-products/${request.product_photo}`}
                                alt={request.product_name}
                                className="w-full max-w-48 h-32 object-cover rounded-lg border mx-auto"
                              />
                            </div>
                          )}
                        </div>

                        <Separator />

                        <div className="flex flex-wrap flex-col sm:flex-row gap-3 justify-between">
                          <div className="flex flex-wrap gap-3">
                            {request.status === "pending" && (
                              <>
                                <Button
                                  onClick={() =>
                                    updateRequestStatus(request.id, "approved")
                                  }
                                  className="bg-green-600 hover:bg-green-700"
                                  size="sm"
                                >
                                  <CheckCircle className="w-4 h-4 mr-1" />
                                  Approve
                                </Button>
                                <Button
                                  onClick={() =>
                                    updateRequestStatus(request.id, "rejected")
                                  }
                                  variant="destructive"
                                  size="sm"
                                >
                                  <XCircle className="w-4 h-4 mr-1" />
                                  Reject
                                </Button>
                              </>
                            )}

                            {request.status === "approved" && (
                              <div className="flex gap-3 items-center">
                                <Select
                                  value={selectedStore}
                                  onValueChange={setSelectedStore}
                                >
                                  <SelectTrigger className="w-48">
                                    <SelectValue placeholder="Pilih toko" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {stores.map((store) => (
                                      <SelectItem
                                        key={store.id}
                                        value={store.id}
                                      >
                                        {store.name}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>

                                <AlertDialog>
                                  <AlertDialogTrigger asChild>
                                    <Button
                                      className="bg-blue-600 hover:bg-blue-700"
                                      size="sm"
                                      disabled={!selectedStore}
                                    >
                                      <Package className="w-4 h-4 mr-1" />
                                      Publish ke Katalog
                                    </Button>
                                  </AlertDialogTrigger>
                                  <AlertDialogContent>
                                    <AlertDialogHeader>
                                      <AlertDialogTitle>
                                        Konfirmasi Publish
                                      </AlertDialogTitle>
                                      <AlertDialogDescription>
                                        Produk "{request.product_name}" akan
                                        dipublish ke katalog toko. Setelah
                                        dipublish, produk akan dapat dilihat
                                        oleh customer. Apakah Anda yakin?
                                      </AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter>
                                      <AlertDialogCancel>
                                        Batal
                                      </AlertDialogCancel>
                                      <AlertDialogAction
                                        onClick={() =>
                                          publishToProducts(request)
                                        }
                                        className="bg-blue-600 hover:bg-blue-700"
                                      >
                                        Ya, Publish
                                      </AlertDialogAction>
                                    </AlertDialogFooter>
                                  </AlertDialogContent>
                                </AlertDialog>
                              </div>
                            )}

                            {request.status === "published" ? (
                              <Badge
                                variant="outline"
                                className="text-blue-600 border-blue-600"
                              >
                                <Package className="w-3 h-3 mr-1" />
                                Sudah Dipublish
                              </Badge>
                            ) : request.status === "rejected" ? (
                              <Badge
                                variant="outline"
                                className="text-red-600 border-red-600"
                              >
                                <XCircle className="w-3 h-3 mr-1" />
                                Produk Tidak Memenuhi Syarat
                              </Badge>
                            ) : request.status === "pending" ? (
                              <Badge
                                variant="outline"
                                className="text-yellow-600 border-yellow-600"
                              >
                                <Clock className="w-3 h-3 mr-1" />
                                Menunggu Persetujuan
                              </Badge>
                            ) : null}
                          </div>

                          {/* Edit and Delete buttons */}
                          <div className="flex gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditRequest(request)}
                            >
                              <Edit className="w-4 h-4 mr-1" />
                              Edit
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteRequest(request.id)}
                              className="text-destructive hover:text-destructive hover:bg-destructive/10"
                            >
                              <Trash2 className="w-4 h-4 mr-1" />
                              Delete
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}

              {/* Edit Dialog */}
              <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Edit Permintaan Titip Jual</DialogTitle>
                  </DialogHeader>
                  {editingRequest && (
                    <form
                      onSubmit={(e) => {
                        e.preventDefault();
                        const formData = new FormData(e.currentTarget);
                        handleUpdateRequest(formData);
                      }}
                      className="space-y-4"
                    >
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="product_name">Nama Produk</Label>
                          <Input
                            id="product_name"
                            name="product_name"
                            defaultValue={editingRequest.product_name}
                            required
                          />
                        </div>
                        <div>
                          <Label htmlFor="product_category">Kategori</Label>
                          <Select
                            name="product_category"
                            defaultValue={editingRequest.product_category}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Fashion & Pakaian">
                                Fashion & Pakaian
                              </SelectItem>
                              <SelectItem value="Elektronik & Gadget">
                                Elektronik & Gadget
                              </SelectItem>
                              <SelectItem value="Makanan & Minuman">
                                Makanan & Minuman
                              </SelectItem>
                              <SelectItem value="Kesehatan & Kecantikan">
                                Kesehatan & Kecantikan
                              </SelectItem>
                              <SelectItem value="Rumah Tangga & Dekorasi">
                                Rumah Tangga & Dekorasi
                              </SelectItem>
                              <SelectItem value="Hobi & Koleksi">
                                Hobi & Koleksi
                              </SelectItem>
                              <SelectItem value="Otomotif & Transport">
                                Otomotif & Transport
                              </SelectItem>
                              <SelectItem value="Buku & Edukasi">
                                Buku & Edukasi
                              </SelectItem>
                              <SelectItem value="Olahraga & Outdoor">
                                Olahraga & Outdoor
                              </SelectItem>
                              <SelectItem value="Lainnya">Lainnya</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label htmlFor="product_price">Harga</Label>
                          <Input
                            id="product_price"
                            name="product_price"
                            type="number"
                            defaultValue={editingRequest.product_price}
                            required
                          />
                        </div>
                        <div>
                          <Label htmlFor="seller_name">Nama Penjual</Label>
                          <Input
                            id="seller_name"
                            name="seller_name"
                            defaultValue={editingRequest.seller_name}
                            required
                          />
                        </div>
                        <div>
                          <Label htmlFor="seller_phone">No. Telp Penjual</Label>
                          <Input
                            id="seller_phone"
                            name="seller_phone"
                            defaultValue={editingRequest.seller_phone}
                            required
                          />
                        </div>
                        <div>
                          <Label htmlFor="provider_bank">Bank</Label>
                          <Input
                            id="provider_bank"
                            name="provider_bank"
                            defaultValue={editingRequest.provider_bank}
                            required
                          />
                        </div>
                        <div className="md:col-span-2">
                          <Label htmlFor="bank_account">No. Rekening</Label>
                          <Input
                            id="bank_account"
                            name="bank_account"
                            defaultValue={editingRequest.bank_account}
                            required
                          />
                        </div>
                        <div className="md:col-span-2">
                          <Label htmlFor="product_description">Deskripsi</Label>
                          <Textarea
                            id="product_description"
                            name="product_description"
                            defaultValue={
                              editingRequest.product_description || ""
                            }
                            rows={3}
                          />
                        </div>
                      </div>
                      <div className="flex justify-end gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => setShowEditDialog(false)}
                        >
                          Batal
                        </Button>
                        <Button type="submit">Simpan Perubahan</Button>
                      </div>
                    </form>
                  )}
                </DialogContent>
              </Dialog>
            </div>
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
};

export default SellRequestManagement;
